import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickOutside } from '@/core/hooks/useClickOutside';
import { useDropdownAutoScroll } from '@/core/hooks/useDropdownAutoScroll';
import { BaseSelect } from '@/core/components/BaseSelect/BaseSelect';
import './Calendar.scss';

const BdsIcon = 'bds-icon' as any;

const getMinDate = () => {
  const date = new Date();
  return new Date(date.getFullYear() - 10, 0, 1);
};

const getMaxDate = () => {
  const date = new Date();
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

interface CalendarProps {
  selectedDate?: Date;
  onDateSelect: (date: Date | undefined) => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  minDate?: Date;
  maxDate?: Date;
}

function formatDateToString(date: Date): string {
  const day   = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year  = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function parseStringToDate(dateString: string): Date | null {
  const parts = dateString.split('/');
  if (parts.length !== 3) return null;

  const day   = Number.parseInt(parts[0], 10);
  const month = Number.parseInt(parts[1], 10) - 1;
  const year  = Number.parseInt(parts[2], 10);

  const date = new Date(year, month, day);
  const isValid =
    date.getFullYear() === year &&
    date.getMonth()    === month &&
    date.getDate()     === day;

  return isValid ? date : null;
}

function clampDateWithLimits(date: Date, minDate: Date, maxDate: Date): Date {
  if (date < minDate) return new Date(minDate);
  if (date > maxDate) return new Date(maxDate);
  return date;
}

function extractYearFromString(dateString: string): number | null {
  const parts = dateString.split('/');
  if (parts.length !== 3) return null;

  const year = Number.parseInt(parts[2], 10);
  return Number.isNaN(year) ? null : year;
}

const getDaysInMonth     = (month: number, year: number) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (month: number, year: number) => new Date(year, month, 1).getDay();

export const Calendar: React.FC<CalendarProps> = ({ 
  selectedDate, 
  onDateSelect,
  isOpen: externalIsOpen,
  onOpenChange,
  minDate: propMinDate,
  maxDate: propMaxDate,
}) => {
  const { t } = useTranslation();

  const MIN_DATE = propMinDate ?? getMinDate();
  const MAX_DATE = propMaxDate ?? getMaxDate();

  const weekdays   = t('globalFilter.weekdays').split(' ');
  const monthNames = t('globalFilter.months').split(' ');

  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = externalIsOpen !== undefined;
  const isOpen = isControlled ? externalIsOpen : internalIsOpen;
  
  const setIsOpen = (open: boolean) => {
    if (!isControlled) {
      setInternalIsOpen(open);
    }
    onOpenChange?.(open);
  };

  const [currentMonth, setCurrentMonth] = useState(
    selectedDate ? selectedDate.getMonth() : new Date().getMonth()
  );
  const [currentYear, setCurrentYear] = useState(
    selectedDate ? selectedDate.getFullYear() : new Date().getFullYear()
  );
  const [dateString, setDateString] = useState(
    selectedDate ? formatDateToString(selectedDate) : ''
  );

  const [prevSelectedDate, setPrevSelectedDate] = useState(selectedDate);
  if (selectedDate !== prevSelectedDate) {
    setPrevSelectedDate(selectedDate);
    setDateString(selectedDate ? formatDateToString(selectedDate) : '');
    if (selectedDate) {
      setCurrentMonth(selectedDate.getMonth());
      setCurrentYear(selectedDate.getFullYear());
    }
  }

  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const applyDate = (date: Date) => {
    const clamped = clampDateWithLimits(date, MIN_DATE, MAX_DATE);
    onDateSelect(clamped);
    setDateString(formatDateToString(clamped));
    setCurrentMonth(clamped.getMonth());
    setCurrentYear(clamped.getFullYear());
  };

  const validateAndClamp = () => {
    const parsed = parseStringToDate(dateString);
    if (parsed) return applyDate(parsed);

    const year = extractYearFromString(dateString);
    if (year !== null) return applyDate(year < MIN_DATE.getFullYear() ? MIN_DATE : MAX_DATE);

    if (dateString === '') return onDateSelect(undefined);

    setDateString(selectedDate ? formatDateToString(selectedDate) : '');
  };

  const closeCalendar = () => {
    validateAndClamp();
    setIsOpen(false);
  };

  useClickOutside(containerRef, closeCalendar, isOpen);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDateString(event.target.value);
  };

  const handleDayClick = (day: number) => {
    const newDate = new Date(currentYear, currentMonth, day);
    onDateSelect(newDate);
    setDateString(formatDateToString(newDate));
  };

  const handlePreviousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(y => y - 1);
      return;
    }
    setCurrentMonth(m => m - 1);
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(y => y + 1);
      return;
    }
    setCurrentMonth(m => m + 1);
  };

  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDay    = getFirstDayOfMonth(currentMonth, currentYear);
  const calendarDays: (number | null)[] = [
    ...new Array<null>(firstDay).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const monthOptions = monthNames.map((name, idx) => ({
    label: name,
    value: String(idx),
  }));

  const yearOptions = Array.from(
    { length: MAX_DATE.getFullYear() - MIN_DATE.getFullYear() + 1 },
    (_, i) => {
      const y = MAX_DATE.getFullYear() - i;
      return { label: String(y), value: String(y) };
    }
  );

  const isPrevDisabled = currentMonth === 0 && currentYear === MIN_DATE.getFullYear();
  const isNextDisabled = currentMonth === MAX_DATE.getMonth() && currentYear === MAX_DATE.getFullYear();

  useDropdownAutoScroll(isOpen, popupRef);

  return (
    <div style={{ position: 'relative', flex: 1 }} ref={containerRef}>
      <label className={`period-calendar-input-box ${isOpen ? 'is-open' : ''}`}>
        <span className="period-calendar-input-hint">
          {t('globalFilter.setDate')}
        </span>
        <input
          type="text"
          className="period-calendar-date-input"
          placeholder="DD/MM/YYYY"
          value={dateString}
          onChange={handleInputChange}
          onClick={() => setIsOpen(true)}
          onFocus={() => setIsOpen(true)}
        />
      </label>

      {isOpen && (
        <div className="period-calendar-popup" ref={popupRef}>
          <div className="period-calendar-header">
            <button
              className="period-calendar-nav-btn"
              onClick={handlePreviousMonth}
              disabled={isPrevDisabled}
              type="button"
              aria-label="Previous month"
            >
              <BdsIcon name="arrow-left" size="small" />
            </button>

            <div className="period-calendar-selectors">
              <BaseSelect
                options={monthOptions}
                value={String(currentMonth)}
                onChange={(val) => setCurrentMonth(Number(val))}
                className="calendar-header-select"
              />
              <BaseSelect
                options={yearOptions}
                value={String(currentYear)}
                onChange={(val) => setCurrentYear(Number(val))}
                className="calendar-header-select"
              />
            </div>

            <button
              className="period-calendar-nav-btn"
              onClick={handleNextMonth}
              disabled={isNextDisabled}
              type="button"
              aria-label="Next month"
            >
              <BdsIcon name="arrow-right" size="small" />
            </button>
          </div>

          <div className="period-calendar-weekdays">
            {weekdays.map((day, idx) => (
              <span key={`${day}-${idx}`} className="period-calendar-weekday">{day}</span>
            ))}
          </div>

          <div className="period-calendar-days-grid">
            {calendarDays.map((day, index) => {
              const isSelected =
                selectedDate?.getDate() === day &&
                selectedDate?.getMonth() === currentMonth &&
                selectedDate?.getFullYear() === currentYear;

              const isFutureDate = !!(day && (
                currentYear > MAX_DATE.getFullYear() ||
                (currentYear === MAX_DATE.getFullYear() && currentMonth > MAX_DATE.getMonth()) ||
                (currentYear === MAX_DATE.getFullYear() && currentMonth === MAX_DATE.getMonth() && day > MAX_DATE.getDate())
              ));

              const isPastDate = !!(day && (
                currentYear < MIN_DATE.getFullYear() ||
                (currentYear === MIN_DATE.getFullYear() && currentMonth < MIN_DATE.getMonth()) ||
                (currentYear === MIN_DATE.getFullYear() && currentMonth === MIN_DATE.getMonth() && day < MIN_DATE.getDate())
              ));

              const key = day ? `day-${day}` : `empty-${index}`;
              const isDisabled = !day || isFutureDate || isPastDate;

              return (
                <button
                  key={key}
                  className={`period-calendar-day ${day ? 'is-clickable' : ''} ${isSelected ? 'is-selected' : ''} ${(isFutureDate || isPastDate) ? 'is-disabled' : ''}`}
                  onClick={() => day && !isFutureDate && !isPastDate && handleDayClick(day)}
                  disabled={isDisabled}
                  type="button"
                  aria-label={day ? `Select day ${day}` : undefined}
                >
                  {day}
                </button>
              );
            })}
          </div>

          <div className="period-calendar-footer">
            <button
              className="period-calendar-btn-reset"
              onClick={() => { onDateSelect(undefined); setDateString(''); }}
              type="button"
            >
              {t('globalFilter.reset')}
            </button>
            <button
              className="period-calendar-btn-confirm"
              onClick={() => { validateAndClamp(); setIsOpen(false); }}
              disabled={!selectedDate}
              type="button"
            >
              {t('globalFilter.confirm')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};